import React, { useState, useEffect } from 'react';
import styles from "./UserDetails.module.css"
import EditableField from '../../components/EditableField/EditableField';
import { IconButton } from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import CancelButton from '../../components/CancelButton/CancelButton';
import SaveButton from '../../components/SaveButton/SaveButton'; 
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import EditButton from '../../components/EditButton/EditButton';
import useProfileQuery from '../../hooks/useProfileQuery';
import { useMutation, useQuery } from '@apollo/client';
import { GET_ALL_TIERS, GET_TIER, GET_USER_PAYMENT_DETAILS } from '../../services/graphql';


export default function UserDetails(){
    const {state} = useLocation();
    const navigate = useNavigate();
    const [isEditing, setEditing] = useState(state?.isEdit || false);
    const [pageLoading, setLoading] = useState(true);
    const [pageTierLoading, setPageLoading] = useState(true);
    const email = sessionStorage.getItem("selectedUser");
    const { loading, data, updateMyProfile, refetch, deleteUser, changeApprovalTier} = useProfileQuery(email);

    const {organization, initName} = useParams();
    const [name, setName] = useState();
    const [numTiers, setNumTiers] = useState();
    const [tierNumber, setTierNumber] = useState(null);

    const derivePaymentMethod = (profile = {}) => {
        const hasVoidCheque = Boolean((profile?.voidChequeFilename || '').trim());
        const hasInteracDetails = Boolean((profile?.interacId || '').trim() && (profile?.securityAnswer || '').trim());
        if (hasVoidCheque) return 'Bank transfer';
        if (hasInteracDetails) return 'Interac e-transfer';
        return 'Not selected';
    };

    const goBack = () => {
        sessionStorage.removeItem("selectedUser");
        navigate(`/user-management`);
    };

    const { data: paymentDetailsData } = useQuery(GET_USER_PAYMENT_DETAILS, {
        variables: { email },
        skip: !email,
    });

    const { tierLoading, tierError, tierData, refetch: tierRefetch } = useQuery(GET_TIER, {
        variables: { email },
        onCompleted(data) {
            setTierNumber(data.getApprover.body.tierNumber);
            setPageLoading(false);
        }
    });

    useEffect(() => {
        if (data && data.getUserProfile && data.getUserProfile.body) {
            const userProfile = data.getUserProfile.body;
            const normalizedUserProfile = {
                ...userProfile,
                canSubmitDistanceInvoices:
                    userProfile?.canSubmitDistanceInvoices === true ||
                    userProfile?.canSubmitDistanceInvoices === 1,
                customDistanceRate: userProfile?.customDistanceRate ?? '',
            };
            setFormData(normalizedUserProfile);
            setLoading(false);
            setFetchedData(normalizedUserProfile);

            if (name === undefined) {
                setName(userProfile.name || initName);
            }

            if (userProfile.accessLevel !== 'Approver') {
                setPageLoading(false);
            }
            tierRefetch(email);
        }

        
    }, [data]);

    useQuery(GET_ALL_TIERS, {
        onCompleted(data) {
            setNumTiers(data.getAllTiers.body.length);
        },
        onError(error) {
            alert('Unable to load tiers.');
        },
    });

    const tierOptions = [];
    tierOptions.push(<option key={0} value="">Select Approval Tier</option>);
    for (let i = 1; i <= numTiers; i++) {
        tierOptions.push(<option key={i} value={i}>{i}</option>);
    }

    const initialFormData = {
        name: '',
        organization: '',
        canSubmitDistanceInvoices: false,
        customDistanceRate: '',
        phoneNumber: '',
        addressLine1: '',
        city:'',
        province: '',
        country: '',
        postalCode: '',
        interacId: '',
        securityAnswer: '',
        voidChequeFilename: '',
        voidChequeFileSize: null,
        voidChequeFileMimeType: '',
    };

    const [formData, setFormData] = useState(initialFormData);
    const [fetchedData, setFetchedData] = useState(initialFormData);
    const [voidChequeBlobUrl, setVoidChequeBlobUrl] = useState('');

    const hasVoidCheque = Boolean((formData.voidChequeFilename || '').trim());
    const hasInteracDetails = Boolean((formData.interacId || '').trim()) && Boolean((formData.securityAnswer || '').trim());
    const isBankTransfer = hasVoidCheque;
    const isETransfer = !isBankTransfer && hasInteracDetails;

    const voidChequeFilename = (formData.voidChequeFilename || '').trim();
    const voidChequeMimeType = paymentDetailsData?.getUserProfile?.body?.voidChequeFileMimeType
        || formData.voidChequeFileMimeType
        || '';
    const voidChequeFileContent = paymentDetailsData?.getUserProfile?.body?.voidChequeFileContent || '';
    const isPdfVoidCheque = (voidChequeMimeType || '').toLowerCase().includes('pdf')
        || /\.pdf$/i.test(voidChequeFilename);
    const isImageVoidCheque = (voidChequeMimeType || '').toLowerCase().startsWith('image/')
        || /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(voidChequeFilename);

    useEffect(() => {
        if (!voidChequeFileContent) {
            setVoidChequeBlobUrl('');
            return;
        }

        try {
            const binary = atob((voidChequeFileContent || '').replace(/\s/g, ''));
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            const fileBlob = new Blob([bytes], { type: voidChequeMimeType || 'application/octet-stream' });
            const objectUrl = URL.createObjectURL(fileBlob);
            setVoidChequeBlobUrl(objectUrl);

            return () => {
                URL.revokeObjectURL(objectUrl);
            };
        } catch (error) {
            console.error('Unable to preview void cheque file:', error);
            setVoidChequeBlobUrl('');
        }
    }, [voidChequeFileContent, voidChequeMimeType]);


  
    const handleSaveClick = async () => {
      const changes = {};

      for (const field in formData) {
          if (formData.hasOwnProperty(field) && fetchedData.hasOwnProperty(field)) {
              //Check if field was modified
              if (formData[field] !== fetchedData[field]) {
                  if (field === 'customDistanceRate') {
                      changes[field] = formData.canSubmitDistanceInvoices && formData[field] !== ''
                          ? Number(formData[field])
                          : null;
                  } else {
                      changes[field] = formData[field];
                  }
              }
          }
      }

      const numericCustomRate = Number(formData.customDistanceRate);
      const invalidCustomRate = formData.canSubmitDistanceInvoices &&
          formData.customDistanceRate !== '' &&
          (!Number.isFinite(numericCustomRate) || numericCustomRate <= 0);
      if (invalidCustomRate) {
          alert('Custom distance rate must be greater than 0.');
          return;
      }

      //If any field was modified, update database
      if (Object.keys(changes).length !== 0) {
          const email = sessionStorage.getItem("selectedUser");
          console.log(changes);

          try {
              await updateMyProfile(email, changes);
              console.log(changes);
              setEditing(false);
              setFetchedData(formData);

              refetch();

              navigate(`/user-management/user/${organization}/${formData.name}`, {state: {isEdit: false}});
              window.location.reload();
          } catch (error) {
              console.error("Error updating profile:", error);
          }
      } else {
        setEditing(false);
      }      

      if (formData.accessLevel === 'Approver') {
        try {
            await changeApprovalTier(email, parseInt(tierNumber, 10));

        } catch (error) {
            console.error("Error updating approval tier:", error);
        }
      }

    };
  
    const handleEditClick = () => {
      setEditing(true);
    };

    const handleChange = (fieldname, fieldvalue) => {
        setFormData((prevData) => ({
            ...prevData,
            [fieldname]:fieldvalue,
            ...(fieldname === 'canSubmitDistanceInvoices' && fieldvalue === false
                ? { customDistanceRate: '' }
                : {}),
        }))
    };

    const handleTierChange = (event) => {
        setTierNumber(event.target.value);
    };

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const customModalStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'var(--theme-bg-elevated)',
            color: 'var(--theme-text)',
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
    };

    useEffect(() => {
        if (deleted) {
            setIsModalOpen(true);
        }
    }, [deleted]);

    const closeModal = () => {
        navigate(`/user-management/user/${organization}/${name}`, {state: {isEdit: false}});
        setIsModalOpen(false);
    };

    const handleDelete = () => {
        //Opens Delete Confirmation window
        setDeleted(true);
    };

    const handleConfirmDelete = async () => {
        try {
            let email = sessionStorage.getItem("selectedUser");
            await deleteUser(email);
            setIsModalOpen(false);
            navigate(`/user-management`);
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    if (!pageLoading && !pageTierLoading)
    return (
    <div className={styles.container}>
        <div className={styles.header}>
            <div style={{width:"30px"}}>
                    <IconButton onClick={goBack} sx={{ color: 'var(--theme-text)' }}>
                    <ArrowBackIosNew />
                    </IconButton>
            </div>
            <h2 className={styles.title}>{name}</h2>
        </div>
        
        <div className={styles.items}>
            <table className={styles.itemTable}>
                <tbody>
                <tr>
                    <th className={styles.label}>Name</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('name', update)} 
                        initialData={name}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>Organization</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('organization', update)} 
                        initialData={organization}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>Email</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('email', update)} 
                        initialData={formData.email}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>Access Level</th>
                    <th className={styles.control}>
                        {isEditing? (<select
                            value={formData.accessLevel}
                            onChange={(update) => handleChange('accessLevel', update.target.value)} 
                            required
                            className={styles.dropdown}
                        >
                            <option value="">Select Access Level</option>
                            <option value="Supplier">Supplier</option>
                            <option value="Approver">Approver</option>
                            <option value="Manager">Manager</option>
                        </select>) : (<EditableField isEditing={false} initialData={formData.accessLevel}/>)}
                    </th>
                </tr>
                {formData.accessLevel === 'Approver' && (
                    <tr>
                        <th className={styles.label}>Approval Tier</th>
                        <th className={styles.control}>
                            {isEditing? (<select
                                value={tierNumber}
                                onChange={handleTierChange}
                                required
                                className={styles.dropdown}
                            >
                                {tierOptions}
                            </select>) : (<EditableField isEditing={false} initialData={tierNumber}/>)}
                        </th>
                    </tr>
                )}
                <tr>
                    <th className={styles.label}>Allow Distance-Based Invoices</th>
                    <th className={styles.control}>
                        {isEditing ? (
                            <select
                                value={formData.canSubmitDistanceInvoices ? 'Yes' : 'No'}
                                onChange={(update) => handleChange('canSubmitDistanceInvoices', update.target.value === 'Yes')}
                                required
                                className={styles.dropdown}
                            >
                                <option value="No">No</option>
                                <option value="Yes">Yes</option>
                            </select>
                        ) : (
                            <EditableField isEditing={false} initialData={formData.canSubmitDistanceInvoices ? 'Yes' : 'No'} />
                        )}
                    </th>
                </tr>
                {formData.canSubmitDistanceInvoices && (
                    <tr>
                        <th className={styles.label}>Custom Distance Rate</th>
                        <th className={styles.control}>
                            {isEditing ? (
                                <input
                                    className={styles.dropdown}
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    placeholder="Default"
                                    value={formData.customDistanceRate ?? ''}
                                    onChange={(update) => handleChange('customDistanceRate', update.target.value)}
                                />
                            ) : (
                                <EditableField
                                    isEditing={false}
                                    initialData={formData.customDistanceRate !== '' && formData.customDistanceRate !== null
                                        ? `$${formData.customDistanceRate}/km`
                                        : 'Default'}
                                />
                            )}
                        </th>
                    </tr>
                )}
                

                <tr>
                    <th className={styles.label}>Phone Number</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('phoneNumber', update)} 
                        initialData={formData.phoneNumber} /></th>
                </tr>

                <tr>
                    <th className={styles.label}>Address Line</th>
                    <th className={styles.control}>
                        <EditableField isEditing={isEditing} 
                        onChange={(update) => handleChange('addressLine1', update)} 
                        initialData={formData.addressLine1}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>City</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('city', update)} 
                        initialData={formData.city}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>Province</th>
                    <th className={styles.control}>
                        <EditableField isEditing={isEditing} 
                        onChange={(update) => handleChange('province', update)} 
                        initialData={formData.province}/></th>
                </tr>

                <tr>
                    <th className={styles.label}>Country</th>
                    <th className={styles.control}>
                        <EditableField 
                        isEditing={isEditing} 
                        onChange={(update) => handleChange('country', update)} 
                        initialData={formData.country}/></th>
                </tr>

                <tr>
                    <th className={styles.label}> Postal Code </th>
                    <th className={styles.control}>
                        <EditableField isEditing={isEditing} 
                        onChange={(update) => handleChange('postalCode', update)} 
                        initialData={formData.postalCode}/></th>
                </tr>

                {isBankTransfer && (
                    <tr>
                        <th className={styles.label}>Payment Method</th>
                        <th className={styles.control}>
                            <EditableField
                            isEditing={false}
                            initialData={derivePaymentMethod(formData)} /></th>
                    </tr>
                )}

                {isETransfer && (
                    <tr>
                        <th className={styles.label}>Payment Method</th>
                        <th className={styles.control}>
                            <EditableField
                            isEditing={false}
                            initialData="Interac e-transfer" /></th>
                    </tr>
                )}

                {!isBankTransfer && !isETransfer && (
                    <tr>
                        <th className={styles.label}>Payment Method</th>
                        <th className={styles.control}>
                            <EditableField
                            isEditing={false}
                            initialData="Not provided" /></th>
                    </tr>
                )}

                {isETransfer && (
                    <tr>
                        <th className={styles.label}>Interac ID</th>
                        <th className={styles.control}>
                            <EditableField
                            isEditing={false}
                            initialData={formData.interacId || ''} /></th>
                    </tr>
                )}

                {isETransfer && (
                    <tr>
                        <th className={styles.label}>Security Answer</th>
                        <th className={styles.control}>
                            <EditableField
                            isEditing={false}
                            initialData={formData.securityAnswer || ''} /></th>
                    </tr>
                )}

                {isBankTransfer && (
                    <tr>
                        <th className={styles.label}>Void Cheque</th>
                        <th className={styles.control}>
                            <div className={styles.voidChequePreviewBox}>
                                {voidChequeBlobUrl && isPdfVoidCheque && (
                                    <div className={styles.voidChequePreviewViewport}>
                                        <embed
                                            src={voidChequeBlobUrl}
                                            type="application/pdf"
                                            className={styles.voidChequePreview}
                                        />
                                    </div>
                                )}
                                {voidChequeBlobUrl && !isPdfVoidCheque && isImageVoidCheque && (
                                    <div className={styles.voidChequePreviewViewport}>
                                        <img
                                            src={voidChequeBlobUrl}
                                            alt="Void cheque preview"
                                            className={styles.voidChequePreview}
                                        />
                                    </div>
                                )}
                                {!voidChequeBlobUrl && (
                                    <div className={styles.voidChequeFallback}>
                                        {voidChequeFilename || 'Void cheque uploaded'}
                                    </div>
                                )}
                                {voidChequeBlobUrl && !isPdfVoidCheque && !isImageVoidCheque && (
                                    <div className={styles.voidChequeFallback}>
                                        Preview unavailable for this file type ({voidChequeFilename || 'file uploaded'}).
                                    </div>
                                )}
                            </div>
                        </th>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
         {isEditing ? (
            <div className={styles.buttonContainer}>
                <SaveButton handleSave={handleSaveClick}/>
                <CancelButton label="CancelUserEdit" url={`/user-management/user/${organization}/${name}`}/> 
            </div>
            ) : (
            <div className={styles.buttonContainer}>
                <EditButton handleEdit={handleEditClick}/>
                <DeleteButton handleDelete={handleDelete}/>
            </div>
        )}
        <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                style={customModalStyles}
                contentLabel="Delete Confirmation"
            >
                <button className={styles.closeButton} onClick={closeModal}>
                    <span className={styles.closeIcon}>x</span>
                </button>
                <h2 className={styles.modalTitle}>Delete User</h2>
                <p className={styles.confirmMessage}>Are you sure you want to delete this user?</p>
                <p className={styles.confirmMessage}>You will not be able to undo this action.</p>
                <DeleteButton handleDelete={handleConfirmDelete}/>
                <CancelButton label="Cancel"/> 
        </Modal>
    </div>
  );
};
